import { defaultPageSize } from '~/apps/search/defaults'
import { SearchFilters } from '~/apps/search/schemas'

const datesURLPart = function* (filters: SearchFilters) {
  if (!filters.dates) {
    return
  }
  yield { filterName: 'arrive', filterValue: filters.dates.arrive }
  yield { filterName: 'depart', filterValue: filters.dates.depart }
}

const categoryIdsURLPart = function* (filters: SearchFilters) {
  if (!filters.categoryIds) {
    return
  }
  for (const category of filters.categoryIds) {
    yield { filterName: 'type', filterValue: category }
  }
}

const pageURLPart = function* (filters: SearchFilters) {
  if (filters.page && filters.page > 1) {
    yield { filterName: 'page', filterValue: filters.page.toString() }
  }
}

const sortURLPart = function* (filters: SearchFilters) {
  if (!filters.sort) {
    return
  }
  yield { filterName: 'sort', filterValue: filters.sort }
}

const facetsURLPart = function* (filters: SearchFilters) {
  if (!filters.facets) {
    return
  }
  for (const facet of filters.facets) {
    yield { filterName: 'facet', filterValue: facet }
  }
}

const limitURLPart = function* (filters: SearchFilters) {
  const limit = filters.limit ?? defaultPageSize
  yield { filterName: 'limit', filterValue: limit.toString() }
}

const hierarchyPathURLPart = function* (filters: SearchFilters) {
  if (!filters.hierarchyPath) {
    return
  }
  yield { filterName: 'path', filterValue: filters.hierarchyPath }
}

const areaURLPart = function* (filters: SearchFilters) {
  // Handle bounds case
  if (filters.bounds) {
    yield { filterName: 'bounds', filterValue: JSON.stringify(filters.bounds) }
    return
  }

  // Handle area case if present
  if (filters.area) {
    const { lat, lng, isEmptyPoint } = filters.area
    if (!isEmptyPoint && typeof lat === 'number' && typeof lng === 'number') {
      yield { filterName: 'lat', filterValue: lat.toString() }
      yield { filterName: 'lng', filterValue: lng.toString() }
    }
  }
}

const partyURLPart = function* (filters: SearchFilters) {
  if (filters.party?.adults && filters.party?.adults !== 2) {
    yield { filterName: 'adults', filterValue: String(filters.party.adults) }
  }
  if (filters.party?.childAges?.length) {
    yield {
      filterName: 'child_ages',
      filterValue: filters.party.childAges.join(','),
    }
  }
  if (filters.party?.dogs) {
    yield { filterName: 'dogs', filterValue: String(filters.party.dogs) }
  }
}

const priceRangeURLPart = function* (filters: SearchFilters) {
  if (!filters.priceRange) {
    return
  }
  if (filters.priceRange.min && !isNaN(filters.priceRange.min)) {
    yield {
      filterName: 'min_price',
      filterValue: filters.priceRange.min.toString(),
    }
  }
  if (filters.priceRange.max && !isNaN(filters.priceRange.max)) {
    yield {
      filterName: 'max_price',
      filterValue: filters.priceRange.max.toString(),
    }
  }
}

const fieldsURLPart = function* (filters: SearchFilters) {
  if (!filters.fields) {
    return
  }
  yield { filterName: 'fields', filterValue: filters.fields.join(',') }
}

const baseURLPart = function* (filters: SearchFilters) {
  if (!filters.base) {
    return
  }
  yield { filterName: 'base', filterValue: filters.base }
}

const withinURLPart = function* (filters: SearchFilters) {
  // Only include within if it's a positive number
  if (typeof filters.within === 'number' && filters.within > 0) {
    yield { filterName: 'within', filterValue: filters.within.toString() }
    return
  }

  // Fall back to area.rangeMiles only if it's a positive number
  if (
    typeof filters.area?.rangeMiles === 'number' &&
    filters.area.rangeMiles > 0
  ) {
    yield {
      filterName: 'within',
      filterValue: filters.area.rangeMiles.toString(),
    }
  }
}

const urlParts = [
  fieldsURLPart,
  datesURLPart,
  categoryIdsURLPart,
  pageURLPart,
  sortURLPart,
  facetsURLPart,
  limitURLPart,
  hierarchyPathURLPart,
  areaURLPart,
  partyURLPart,
  priceRangeURLPart,
  baseURLPart,
  withinURLPart,
]

export function* parseSearchFiltersToUrl(filters: SearchFilters) {
  for (const funcToRun of urlParts) {
    for (const cleaned of funcToRun(filters)) {
      yield cleaned
    }
  }
}
